import type { RequestParams } from '../requests/http-client';
import { ContentType, HttpClient } from '../requests/http-client';
import type {
  AddVerificatorEventFilterReq,
  BaseErrorResponse,
  CreateVerificatorReq,
  Cs2EventFilterVerificatorVM,
  Cs2VerificatorVM,
  EmptySuccessResponse,
  UpdateVerificatorEventFilterReq,
  UpdateVerificatorReq,
  VerificatorIdParams,
} from './data-contracts';


export default function useVerificator() {
  class Verificator<SecurityDataType = unknown> extends HttpClient<SecurityDataType> {
    /**
     * No description
     *
     * @tags Verificator
     * @name VerificatorControllerGetVerificators
     * @summary Get verificators
     * @request GET:/cs2/verificator
     * @secure
     * @response `200` `(Cs2VerificatorVM)[]`
     * @response `400` `BaseErrorResponse`
     */
    verificatorControllerGetVerificators = (
      query?: {
        /** @example 1 */
        verificatorClassId?: number;
      },
      params: RequestParams = {},
    ) =>
      this.request<Cs2VerificatorVM[], BaseErrorResponse>({
        path: '/cs2/verificator',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Verificator
     * @name VerificatorControllerCreateVerificator
     * @summary Create verificator
     * @request POST:/cs2/verificator
     * @secure
     * @response `200` `Cs2VerificatorVM`
     * @response `400` `BaseErrorResponse`
     */
    verificatorControllerCreateVerificator = (data: CreateVerificatorReq, params: RequestParams = {}) =>
      this.request<Cs2VerificatorVM, BaseErrorResponse>({
        path: '/cs2/verificator',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Verificator
     * @name VerificatorControllerUpdateVerificator
     * @summary Update verificator
     * @request PUT:/cs2/verificator/{verificatorId}
     * @secure
     * @response `200` `Cs2VerificatorVM`
     * @response `400` `BaseErrorResponse`
     */
    verificatorControllerUpdateVerificator = (
      verificatorId: number,
      data: UpdateVerificatorReq,
      params: RequestParams = {},
    ) =>
      this.request<Cs2VerificatorVM, BaseErrorResponse>({
        path: `/cs2/verificator/${verificatorId}`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Verificator
     * @name VerificatorControllerDeleteVerificator
     * @summary Delete verificator
     * @request DELETE:/cs2/verificator/{verificatorId}
     * @secure
     * @response `200` `VerificatorIdParams`
     * @response `400` `BaseErrorResponse`
     */
    verificatorControllerDeleteVerificator = (verificatorId: number, params: RequestParams = {}) =>
      this.request<VerificatorIdParams, BaseErrorResponse>({
        path: `/cs2/verificator/${verificatorId}`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Verificator
     * @name VerificatorEventFilterControllerAddEventFilter
     * @summary Create event filter
     * @request POST:/cs2/verificator/{verificatorId}/event-filter
     * @secure
     * @response `200` `Cs2EventFilterVerificatorVM`
     * @response `400` `BaseErrorResponse`
     */
    verificatorEventFilterControllerAddEventFilter = (
      verificatorId: number,
      data: AddVerificatorEventFilterReq,
      params: RequestParams = {},
    ) =>
      this.request<Cs2EventFilterVerificatorVM, BaseErrorResponse>({
        path: `/cs2/verificator/${verificatorId}/event-filter`,
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Verificator
     * @name VerificatorEventFilterControllerUpdateEventFilter
     * @summary Update event filter
     * @request PUT:/cs2/verificator/{verificatorId}/event-filter/{eventFilterVerificatorId}
     * @secure
     * @response `200` `Cs2EventFilterVerificatorVM`
     * @response `400` `BaseErrorResponse`
     */
    verificatorEventFilterControllerUpdateEventFilter = (
      verificatorId: number,
      eventFilterVerificatorId: number,
      data: UpdateVerificatorEventFilterReq,
      params: RequestParams = {},
    ) =>
      this.request<Cs2EventFilterVerificatorVM, BaseErrorResponse>({
        path: `/cs2/verificator/${verificatorId}/event-filter/${eventFilterVerificatorId}`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Verificator
     * @name VerificatorEventFilterControllerDeleteEventFilter
     * @summary Delete event filter
     * @request DELETE:/cs2/verificator/{verificatorId}/event-filter/{eventFilterVerificatorId}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    verificatorEventFilterControllerDeleteEventFilter = (
      verificatorId: number,
      eventFilterVerificatorId: number,
      params: RequestParams = {},
    ) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/cs2/verificator/${verificatorId}/event-filter/${eventFilterVerificatorId}`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });
  }

  return new Verificator();
}
